import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Crown, ExternalLink } from 'lucide-react';
import HostLayout from '@/components/mafia/HostLayout';
import { useMafiaAuth } from '@/context/MafiaAuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function HostPlayersPage() {
  const navigate = useNavigate();
  const { host, loading } = useMafiaAuth();
  const [players, setPlayers] = useState([]);
  const [q, setQ] = useState('');

  useEffect(() => { if (!loading && !host) navigate('/mafia/login', { replace: true }); }, [loading, host, navigate]);
  useEffect(() => {
    if (!host) return;
    fetch(`${API}/api/mafia/auth/leaderboard?limit=200`).then(r => r.json())
      .then(r => setPlayers(r.leaderboard || [])).catch(() => {});
  }, [host]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return players;
    return players.filter(p => (p.name || '').toLowerCase().includes(s) || (p.member_no || '').toLowerCase().includes(s));
  }, [players, q]);

  if (loading || !host) return <div className="ms-page flex items-center justify-center ms-text-3">Loading…</div>;

  return (
    <HostLayout title="Игроки" subtitle={`${players.length} зарегистрировано`}>
      <div className="max-w-4xl">
        <div className="relative mb-6">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 ms-text-3" />
          <input
            placeholder="Поиск по имени или номеру…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="players-search"
            className="ms-input pl-9"
          />
        </div>

        <section className="ms-card" data-testid="players-table">
          <div className="hidden md:grid grid-cols-12 px-5 py-3 ms-border-b">
            <span className="col-span-1 ms-kicker">#</span>
            <span className="col-span-5 ms-kicker">Имя</span>
            <span className="col-span-2 ms-kicker">Игр</span>
            <span className="col-span-2 ms-kicker">Побед</span>
            <span className="col-span-2 ms-kicker text-right">Очки</span>
          </div>
          {filtered.length === 0 ? (
            <p className="px-5 py-12 text-sm ms-text-2 text-center">Никто не найден.</p>
          ) : (
            <div className="ms-divide">
              {filtered.map((p, i) => (
                <div key={p.member_no} className="grid grid-cols-2 md:grid-cols-12 items-center px-5 py-3 hover:bg-[var(--ms-surface-2)] transition-colors group" data-testid={`player-row-${p.member_no}`}>
                  <span className="md:col-span-1 text-sm ms-text-3 tabular-nums flex items-center gap-1.5">
                    {i < 3 && <Crown size={12} className={i === 0 ? 'ms-text-ember' : 'ms-text-3'} />}
                    {i + 1}
                  </span>
                  <button onClick={() => navigate(`/p/${p.member_no}`)} className="md:col-span-5 text-left min-w-0">
                    <p className="text-sm ms-text truncate group-hover:ms-text-red transition-colors">{p.name}</p>
                    <p className="text-[11px] ms-text-3 mt-0.5">{p.member_no}</p>
                  </button>
                  <span className="md:col-span-2 text-sm ms-text-2 tabular-nums">{p.games_played}</span>
                  <span className="md:col-span-2 text-sm ms-text-2 tabular-nums">{p.wins}</span>
                  <span className="md:col-span-2 text-right">
                    <span className="ms-chip ms-chip-red font-semibold tabular-nums">{p.points}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </HostLayout>
  );
}
